import { WORKROOMS_BASE_URL } from '../../constants/endpoint'
import { IHasSubscription } from '../models/subscription.model'
import { IAntiforgeryToken, IIdObject } from '../models/general.model'
import {
  IWorkroom,
  IWorkroomComment,
  IWorkroomDocument,
  IWorkroomExtended,
  IWorkroomFolder,
  IWorkroomMember,
  IWorkroomPost,
  IWorkroomPosts,
  IWorkrooms,
  IWorkroomUserPermissions,
} from '../models/workrooms.model'
import { getAntiforgeryToken } from './general.service'

export async function getAllNotHiddenWorkrooms(): Promise<IWorkroom[]> {
  let response = await fetch(`${WORKROOMS_BASE_URL}?isHidden=false`)

  const jsonResults = await response.json()
  return <IWorkroom[]>jsonResults
}

export async function getAllWorkrooms(pageIndex?: number): Promise<IWorkrooms> {
  let response = await fetch(`${WORKROOMS_BASE_URL}?getResultPaged=true${pageIndex ? `&pageIndex=${pageIndex}` : ''}`)

  const jsonResults = await response.json()
  return <IWorkrooms>jsonResults
}

export async function getMyWorkrooms(): Promise<IWorkroom[]> {
  let response = await fetch(`${WORKROOMS_BASE_URL}?getOnlyMyWorkrooms=true&isHidden=false`)

  const jsonResults = await response.json()
  return <IWorkroom[]>jsonResults
}

export async function getWorkroomsByCategory(activeType: number): Promise<IWorkroom[]> {
  let response = await fetch(`${WORKROOMS_BASE_URL}?workroomCategoryId=${activeType}&isHidden=false`)

  const jsonResults = await response.json()
  return <IWorkroom[]>jsonResults
}

export async function joinWorkroom(token: string, workroomId: number): Promise<any> {
  await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/addmembershipforcurrentuser`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
  })

  return true
}

export async function leaveWorkroom(token: string, workroomId: number): Promise<Boolean> {
  await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/removemembershipforcurrentuser`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
  })
  return true
}

export async function getSpecificWorkroom(workroomId: number): Promise<IWorkroom> {
  let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}`)

  const jsonResults = await response.json()
  return <IWorkroom>jsonResults
}

export async function getWorkroomMembers(workroomId: number): Promise<IWorkroomMember[]> {
  let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/members`)

  const jsonResults = await response.json()
  return <IWorkroomMember[]>jsonResults
}

export async function createWorkroom(
  token: string,
  name: string,
  description: string,
  reason: string,
  type: number | null,
  isHidden: boolean,
): Promise<boolean> {
  const bodyWorkroom = JSON.stringify({
    Name: name,
    Description: description,
    CreationReasonText: reason,
    WorkroomCategoryId: type,
    IsHidden: isHidden,
  })

  let response = await fetch(`${WORKROOMS_BASE_URL}/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
    body: bodyWorkroom,
  })
  const successfullyCreated = (await response.ok) ?? response.ok
  return <boolean>successfullyCreated
}

export async function updateWorkroom(
  token: string,
  workroomId: number,
  name: string,
  description: string,
  type: number | null,
  isHidden: boolean,
): Promise<boolean> {
  const bodyWorkroom = JSON.stringify({
    Name: name,
    Description: description,
    WorkroomCategoryId: type,
    IsHidden: isHidden,
  })

  let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
    body: bodyWorkroom,
  })
  const successfullyCreated = (await response.ok) ?? response.ok
  return <boolean>successfullyCreated
}

export async function getWorkroomPosts(workroomId: number, pageIndex: number): Promise<IWorkroomPosts> {
  let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/posts?pageIndex=${pageIndex}`)

  const jsonResults = await response.json()
  return <IWorkroomPosts>jsonResults
}

export async function getWorkroomPost(workroomId: number, postId: number): Promise<IWorkroomPost> {
  let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/posts/${postId}`)

  const jsonResults = await response.json()
  return <IWorkroomPost>jsonResults
}

export async function createWorkroomPost(
  token: string,
  id: number,
  title: string,
  mainbody: string,
  emailNotification: boolean,
): Promise<IIdObject> {
  const bodyWorkroomPost = JSON.stringify({
    Title: title,
    Mainbody: mainbody,
    NotifyAllWorkroomMembers: emailNotification,
  })

  let response = await fetch(`${WORKROOMS_BASE_URL}/${id}/posts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
    body: bodyWorkroomPost,
  })
  const jsonResults = await response.json()
  return <IIdObject>jsonResults
}

export async function deleteWorkroomPost(token: string, workroomId: number, workroomPostId: number): Promise<Boolean> {
  await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/posts/${workroomPostId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
  })
  return true
}

export async function updateWorkroomPost(
  token: string,
  workroomId: number,
  workroomPostId: number,
  title: string,
  mainbody: string,
) {
  const bodyWorkroomPost = JSON.stringify({
    Title: title,
    MainBody: mainbody,
  })

  await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/posts/${workroomPostId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
    body: bodyWorkroomPost,
  })
  return
}

export async function getWorkroomPostComments(workroomId: number, postId: number): Promise<IWorkroomComment[]> {
  let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/posts/${postId}/comments`)

  const jsonResults = await response.json()
  return <IWorkroomComment[]>jsonResults
}

export async function deleteWorkroomPostComment(
  token: string,
  workroomId: number,
  workroomPostId: number,
  workroomPostCommentId: number,
): Promise<Boolean> {
  await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/posts/${workroomPostId}/comments/${workroomPostCommentId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
  })
  return true
}

export async function updateWorkroomPostComment(
  token: string,
  workroomId: number,
  workroomPostId: number,
  workroomPostCommentId: number,
  mainbody: string,
) {
  const bodyWorkroomPost = JSON.stringify({
    Text: mainbody,
  })

  await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/posts/${workroomPostId}/comments/${workroomPostCommentId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
    body: bodyWorkroomPost,
  })
  return
}

export async function createWorkroomPostComment(
  token: string,
  workroomId: number,
  workroomPostId: number,
  mainbody: string,
): Promise<IIdObject> {
  const bodyWorkroomPost = JSON.stringify({
    Text: mainbody,
  })

  let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/posts/${workroomPostId}/comments`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
    body: bodyWorkroomPost,
  })
  const jsonResults = await response.json()
  return <IIdObject>jsonResults
}

export async function deleteWorkroom(workroomId: number): Promise<Boolean> {
  const { csrfToken }: IAntiforgeryToken = await getAntiforgeryToken()

  await fetch(`${WORKROOMS_BASE_URL}/${workroomId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
  })
  return true
}

export async function isCurrentUserMember(workroomId: number): Promise<boolean> {
  let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/hascurrentusermembership`)

  const jsonResults = await response.json()
  return <boolean>jsonResults
}

export async function removeMembershipFromUser(token: string, workroomId: number, userId: number): Promise<Boolean> {
  let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/removemembership?userId=${userId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
  })
  const successfullyRemoved = response.ok ?? response.ok
  return <Boolean>successfullyRemoved
}

export async function removeMembershipFromCurrentUser(token: string, workroomId: number): Promise<Boolean> {
  let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/removemembershipforcurrentuser`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
  })
  const successfullyRemoved = response.ok ?? response.ok
  return <Boolean>successfullyRemoved
}

export async function addWorkroomAdmin(token: string, workroomId: number, userId: number): Promise<Boolean> {
  let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/addworkroomadmin?userId=${userId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
  })
  const successfullyAdded = response.ok ?? response.ok
  return <Boolean>successfullyAdded
}

export async function removeWorkroomAdmin(token: string, workroomId: number, userId: number): Promise<Boolean> {
  let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/removeworkroomadmin?userId=${userId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
  })
  const successfullyRemoved = response.ok ?? response.ok
  return <Boolean>successfullyRemoved
}

export async function getMyWorkroomPermissions(workroomId: number): Promise<IWorkroomUserPermissions> {
  let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/mypermissions`)

  const jsonResults = await response.json()
  return <IWorkroomUserPermissions>jsonResults
}

export async function addWorkroomPostSubscription(token: string, workroomId: number, postId: number): Promise<boolean> {
  let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/posts/${postId}/subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
  })
  const jsonResults = (await response.ok) ?? response.ok
  return jsonResults
}

export async function hasUserSubscribedToWorkroomPost(workroomId: number, postId: number): Promise<IHasSubscription> {
  let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/posts/${postId}/subscribe`)
  const isSubscribing = await response.json()
  return <IHasSubscription>isSubscribing
}

export async function removeWorkroomPostSubscription(
  token: string,
  workroomId: number,
  postId: number,
): Promise<Boolean> {
  await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/posts/${postId}/subscribe`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
  })
  return true
}

// Workroom documents
export const getFiles = async (workroomId: string, currentFolder: string): Promise<IWorkroomDocument[]> => {
  let response = fetch(`/api/community/workrooms/${workroomId}/documents?folderId=${currentFolder}`)
  let files = (await response).json()
  return files
}

export const createNewFile = async (
  workroomId: string,
  folderId: string | null,
  name: string,
  file: File,
): Promise<Boolean> => {
  const { csrfToken }: IAntiforgeryToken = await getAntiforgeryToken()

  const formData = new FormData()
  formData.append('File', file)
  formData.append('FileName', name)

  if (folderId) formData.append('FolderId', folderId)

  let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/documents`, {
    method: 'POST',
    headers: {
      'X-CSRF-TOKEN': csrfToken,
    },
    body: formData,
  })

  if (!response.ok) {
    throw new Error(`Failed to create file: ${response.statusText}`)
  }

  return true
}

export const updateFile = async (WorkroomId: string, file: IWorkroomDocument): Promise<Boolean> => {
  const { csrfToken }: IAntiforgeryToken = await getAntiforgeryToken()

  const fileBody = JSON.stringify({
    Filename: file.name,
    FolderId: file.workroomFolderId,
  })

  let response = await fetch(`${WORKROOMS_BASE_URL}/${WorkroomId}/documents/${file.id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: fileBody,
  })

  if (!response.ok) {
    throw new Error(`Failed to update file ${file.name}: ${response.statusText}`)
  }

  return response.ok
}

export const deleteFile = async (workroomId: string, fileId: string): Promise<Boolean> => {
  const { csrfToken }: IAntiforgeryToken = await getAntiforgeryToken()

  const response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/documents/${fileId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
  })

  return response.ok
}

// Workroom folders
export const getFolder = async (workroomId: string, folderId: string): Promise<IWorkroomFolder> => {
  let response = fetch(`/api/community/workrooms/${workroomId}/documentfolders/${folderId}`)
  let folders = (await response).json()
  return folders
}

export const getFolders = async (workroomId: string, currentFolder: string): Promise<IWorkroomFolder[]> => {
  let response = fetch(`/api/community/workrooms/${workroomId}/documentfolders?parentFolderId=${currentFolder}`)
  let folders = (await response).json()
  return folders
}

export const createNewFolder = async (
  workroomId: string,
  name: string,
  parentWorkroomFolderId: string | null,
): Promise<Boolean> => {
  try {
    const { csrfToken }: IAntiforgeryToken = await getAntiforgeryToken()

    let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/documentfolders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify({
        Name: name,
        ParentWorkroomFolderId: parentWorkroomFolderId,
      }),
    })
    const jsonResults = (await response.ok) ?? response.ok
    return jsonResults
  } catch {
    console.error('Det gick inte att skapa ny mapp')
  }

  return false
}

export const updateFolder = async (workroomId: string, folder: IWorkroomFolder): Promise<Boolean> => {
  const { csrfToken }: IAntiforgeryToken = await getAntiforgeryToken()

  const folderBody = JSON.stringify({
    Name: folder.name,
    ParentWorkroomFolderId: folder.parentWorkroomFolderId,
  })

  let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/documentfolders/${folder.id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: folderBody,
  })

  if (!response.ok) {
    throw new Error(`Failed to update folder ${folder.name}: ${response.statusText}`)
  }

  return response.ok
}

export const deleteFolder = async (workroomId: string, folderId: string): Promise<Boolean> => {
  const { csrfToken }: IAntiforgeryToken = await getAntiforgeryToken()

  const response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/documentfolders/${folderId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
  })

  return response.ok
}

// Breadcrumbs
export const getAncestors = async (workroomId: string, folderId: string): Promise<IWorkroomFolder[]> => {
  const token: IAntiforgeryToken = await getAntiforgeryToken()

  const response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/documentfolders/${folderId}/ancestors`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token.csrfToken,
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch breadcrumb: ${response.statusText}`)
  }

  const data: IWorkroomFolder[] = await response.json()

  return data
}

//Hidden workrooms
export const getAllHiddenWorkrooms = async (): Promise<IWorkroomExtended[]> => {
  let response = fetch(`${WORKROOMS_BASE_URL}?isHidden=true`)
  let hiddenWorkrooms = (await response).json()
  return hiddenWorkrooms
}

//Not approved workrooms
export const getAllNotApprovedWorkrooms = async (): Promise<IWorkroomExtended[]> => {
  let response = fetch(`${WORKROOMS_BASE_URL}?isApproved=false`)
  let notApprovedWorkrooms = (await response).json()
  return notApprovedWorkrooms
}

//Change workroom owner
export const changeWorkroomOwner = async (workroomId: number, userId: number): Promise<Boolean> => {
  const { csrfToken }: IAntiforgeryToken = await getAntiforgeryToken()

  const userBody = JSON.stringify({
    UserId: userId,
  })

  let response = await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/owner`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: userBody,
  })

  if (!response.ok) {
    throw new Error(`Failed to update owner: ${response.statusText}`)
  }

  return response.ok
}

export async function approveWorkroom(workroomId: number): Promise<any> {
  const { csrfToken }: IAntiforgeryToken = await getAntiforgeryToken()

  await fetch(`${WORKROOMS_BASE_URL}/${workroomId}/approve`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
  })

  return true
}
